import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { ProjectsDashboardService, DashboardProject } from '../../services/projects-dashboard.service';
import { ToolsService, SaveNoteNotificationRequest } from '../../services/tools.service';

@Component({
  selector: 'app-notes-notification',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent],
  templateUrl: './notes-notification.component.html',
  styleUrls: ['./notes-notification.component.css'],
})
export class NotesNotificationComponent implements OnInit {
  // Form fields (mirrors NotesNotificationViewModel)
  notes: string = '';
  notesStartDate: string = this.toDateInputValue(new Date());
  notesEndDate: string = this.toDateInputValue(new Date(Date.now() + 86400000));
  selectedProjectId: number | null = null;
  selectedProjectName: string = '';

  // Year selector (mirrors ChangeYearCommand + Years/CurrentYear)
  currentYear: number = new Date().getFullYear();
  years: number[] = this.buildYears(5);

  // Data
  projects: DashboardProject[] = [];


  // UI state
  isLoadingProjects: boolean = false;
  isLoadingNotes: boolean = false;
  isSaving: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  constructor(
    private projectsService: ProjectsDashboardService,
    private toolsService: ToolsService,
    public dialogRef: MatDialogRef<NotesNotificationComponent>
  ) {}

  ngOnInit(): void {
    //this.loadNotes();
    this.loadProjects(this.currentYear);
  }

  onYearChange(year: number): void {
    this.currentYear = year;
    this.selectedProjectId = null;
    this.selectedProjectName = '';
    this.loadProjects(year);
  }

  onProjectChange(projectId: number): void {
    const project = this.projects.find(p => p.id == projectId);
    this.selectedProjectName = project ? project.name : '';
  }

  saveNote(): void {    
    this.clearMessages();

    if (!this.notes.trim()) {
      this.errorMessage = 'Notes text is required.';
      return;
    }
    if (!this.selectedProjectId) {
      this.errorMessage = 'A project must be selected.';
      return;
    }
    if (!this.notesStartDate || !this.notesEndDate) {
      this.errorMessage = 'Start date and end date are required.';
      return;
    }
    if (new Date(this.notesEndDate) < new Date(this.notesStartDate)) {
      this.errorMessage = 'End date must be on or after start date.';
      return;
    }

    const request: SaveNoteNotificationRequest = {
      notes: this.notes.trim(),
      notesStartDate: this.notesStartDate,
      notesEndDate: this.notesEndDate,
      projectId: this.selectedProjectId,
      projectName: this.selectedProjectName,
    };

    this.isSaving = true;
    this.toolsService.saveNote(request).subscribe({
      next: () => {
        this.successMessage = 'Note added successfully.';
        this.resetForm();
        //this.loadNotes();
        this.isSaving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage = err.error?.message ?? 'Unable to add note. Please try again.';
        this.isSaving = false;
      },
    });
  }

  clearMessages(): void {
    this.errorMessage = null;
    this.successMessage = null;
  }

  get isFormValid(): boolean {
    return (
      !!this.notes.trim() &&
      !!this.selectedProjectId &&
      !!this.notesStartDate &&
      !!this.notesEndDate &&
      new Date(this.notesEndDate) >= new Date(this.notesStartDate)
    );
  }

  private loadProjects(year: number): void {
    this.isLoadingProjects = true;
    this.projects = [];
    this.projectsService.getProjectsByYear(year).subscribe({
      next: (data) => {
        this.projects = data.slice().sort((a, b) => b.name.localeCompare(a.name));
        this.isLoadingProjects = false;
      },
      error: () => {
        this.isLoadingProjects = false;
      },
    });
  }

  

  private resetForm(): void {
    this.notes = '';
    this.notesStartDate = this.toDateInputValue(new Date());
    this.notesEndDate = this.toDateInputValue(new Date(Date.now() + 86400000));
    this.selectedProjectId = null;
    this.selectedProjectName = '';
  }

  private buildYears(count: number): number[] {
    const current = new Date().getFullYear();
    return Array.from({ length: count }, (_, i) => current - i);
  }

  private toDateInputValue(date: Date): string {
    return date.toISOString().substring(0, 10);
  }
}
